import React, { useState } from "react";
import { X, Minus, Plus, Trash2, Ticket, CreditCard, Landmark, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useTrolley } from "../../context/TrolleyContext";
import { useCustomer } from "../../context/CustomerContext";

export const TrolleyDrawer = ({ settings }) => {
  const { items, setQty, remove, clear, subtotal, open, setOpen } = useTrolley();
  const { customer } = useCustomer();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [shipMethod, setShipMethod] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  React.useEffect(() => {
    if (customer && !form.email) setForm((f) => ({ ...f, name: f.name || customer.name || "", email: customer.email }));
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const methods = settings?.shipping_methods || [];
  const activeShip = methods.find((m) => m.id === shipMethod) || methods[0];
  const freeOver = Number(settings?.shipping_free_over || 0);
  const freeShip = (freeOver && subtotal >= freeOver) || voucher?.type === "free_shipping";
  const shipping = freeShip ? 0 : Number(activeShip?.price || settings?.shipping_flat || 0);
  let voucherSaving = 0;
  if (voucher) {
    if (voucher.type === "percent") voucherSaving = (subtotal * voucher.value) / 100;
    else if (voucher.type === "fixed") voucherSaving = Math.min(voucher.balance, subtotal + shipping);
  }
  const total = Math.max(0, subtotal - voucherSaving + shipping);
  const gstRate = Number(settings?.gst_rate || 0);
  const gst = gstRate ? (total * gstRate) / (100 + gstRate) : 0;

  const checkVoucher = async () => {
    try {
      const { data } = await api.post("/vouchers/check", { code: voucherInput, subtotal });
      setVoucher(data);
      toast.success(`Voucher ${data.code} applied!`);
    } catch (e) {
      setVoucher(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/supply/orders", {
        customer: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        notes: form.notes,
        shipping_method_id: activeShip?.id || "",
        voucher_code: voucher?.code || "",
        payment_method: paymentMethod,
      });
      clear();
      if (paymentMethod === "card" && data.total > 0) {
        const res = await api.post("/payments/order-checkout", { order_id: data.id, origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
        return;
      }
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="trolley-drawer">
      <div className="absolute inset-0 bg-plum-deep/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-cream border-l-[3px] border-plum-deep overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-magenta text-cream px-5 py-4 flex items-center justify-between z-10">
          <h2 className="font-serif-display uppercase text-lg tracking-[0.12em]">YOUR GARDEN TROLLEY</h2>
          <button onClick={() => setOpen(false)} data-testid="trolley-close" aria-label="Close trolley" className="w-8 h-8 rounded-full bg-cream/15 hover:bg-cream/30 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-4" data-testid="supply-order-success">
            <Flower2 className="w-14 h-14 text-magenta mx-auto animate-bloom-pop" />
            <h3 className="font-serif-display uppercase text-plum-deep text-2xl">ORDER {done.order_number} PLANTED!</h3>
            <p className="text-sm text-plum-deep/75">A confirmation is on its way to your inbox.</p>
            {done.total > 0 && (
              <div className="bg-warmwhite border-2 border-plum-deep/15 rounded-2xl p-4 text-left text-sm">
                <p className="font-bold uppercase text-[11px] tracking-[0.16em] mb-1">PAY BY BANK TRANSFER</p>
                <p>{settings?.bank_account_name}</p>
                <p className="font-bold tracking-wide">{settings?.bank_account_number}</p>
                <p className="text-xs text-plum-deep/60 mt-1">Reference: {done.order_number} · ${done.total.toFixed(2)} NZD</p>
              </div>
            )}
            <button onClick={() => { setDone(null); setOpen(false); }} data-testid="trolley-keep-shopping" className="rounded-full bg-plum text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-bold">
              KEEP BROWSING
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <p className="font-serif-display uppercase text-plum-deep/60 tracking-[0.1em]" data-testid="trolley-empty">Your trolley is feeling light as a petal.</p>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <ul className="space-y-3">
              {items.map((it) => (
                <li key={it.key} className="flex gap-3 bg-warmwhite border-2 border-plum-deep/10 rounded-2xl p-3" data-testid={`trolley-item-${it.product_id}`}>
                  {it.image && <img src={it.image} alt="" className="w-16 h-16 rounded-xl object-cover border border-plum-deep/10" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-plum-deep truncate">{it.title}</p>
                    {it.variant_title && <p className="text-[11px] text-plum-deep/60 truncate">{it.variant_title}</p>}
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => setQty(it.key, it.quantity - 1)} className="w-6 h-6 rounded-full bg-blush/60 flex items-center justify-center" aria-label="Less" data-testid={`trolley-minus-${it.product_id}`}><Minus className="w-3 h-3" /></button>
                      <span className="text-sm font-bold w-5 text-center">{it.quantity}</span>
                      <button onClick={() => setQty(it.key, it.quantity + 1)} className="w-6 h-6 rounded-full bg-blush/60 flex items-center justify-center" aria-label="More" data-testid={`trolley-plus-${it.product_id}`}><Plus className="w-3 h-3" /></button>
                      <span className="ml-auto text-sm font-bold">${(it.price * it.quantity).toFixed(2)}</span>
                      <button onClick={() => remove(it.key)} aria-label="Remove" data-testid={`trolley-remove-${it.product_id}`} className="text-plum-deep/40 hover:text-magenta"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="bg-lemon/25 border-2 border-lemon rounded-2xl p-3">
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-plum-deep mb-2 flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> GOT A VOUCHER?</p>
              <div className="flex gap-2">
                <input value={voucherInput} onChange={(e) => setVoucherInput(e.target.value.toUpperCase())} placeholder="CODE" data-testid="trolley-voucher-input"
                  className="flex-1 rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm uppercase focus:outline-none focus:border-magenta" />
                <button onClick={checkVoucher} data-testid="trolley-voucher-apply" className="rounded-full bg-plum text-cream px-4 py-2 text-[11px] uppercase font-bold tracking-[0.1em]">APPLY</button>
              </div>
              {voucher && <p className="text-xs text-forest font-semibold mt-2" data-testid="trolley-voucher-applied">✓ {voucher.code} applied{voucherSaving > 0 ? ` — saving $${voucherSaving.toFixed(2)}` : voucher.type === "free_shipping" ? " — free shipping" : ""}</p>}
            </div>

            {methods.length > 1 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-plum-deep mb-2">SHIPPING</p>
                {methods.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <input type="radio" name="supply-ship" checked={(activeShip?.id) === m.id} onChange={() => setShipMethod(m.id)} className="accent-magenta" />
                    {m.name} — {freeShip ? "FREE" : `$${Number(m.price).toFixed(2)}`}
                  </label>
                ))}
              </div>
            )}

            <div className="text-sm space-y-1 border-t-2 border-dashed border-plum-deep/20 pt-3" data-testid="trolley-totals">
              <p className="flex justify-between"><span>SUBTOTAL</span><span>${subtotal.toFixed(2)}</span></p>
              {voucherSaving > 0 && <p className="flex justify-between text-forest"><span>VOUCHER</span><span>−${voucherSaving.toFixed(2)}</span></p>}
              <p className="flex justify-between"><span>SHIPPING</span><span>{shipping ? `$${shipping.toFixed(2)}` : "FREE 🌼"}</span></p>
              <p className="flex justify-between font-bold text-lg text-plum-deep"><span>TOTAL</span><span data-testid="trolley-total">${total.toFixed(2)} NZD</span></p>
              {gstRate > 0 && <p className="text-right text-[11px] text-plum-deep/50">Includes GST ({gstRate}%): ${gst.toFixed(2)}</p>}
              {freeOver > 0 && !freeShip && <p className="text-[11px] text-magenta font-semibold">Add ${(freeOver - subtotal).toFixed(2)} more for free shipping!</p>}
            </div>

            <form onSubmit={placeOrder} className="bg-plum text-cream rounded-[1.5rem] p-5 space-y-3" data-testid="supply-checkout-form">
              <h4 className="font-serif-display uppercase tracking-[0.1em]">YOUR DETAILS</h4>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" data-testid="supply-name" className="w-full rounded-full px-4 py-2.5 bg-cream text-plum-deep text-sm placeholder:text-plum-deep/50 focus:outline-none" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="supply-email" className="w-full rounded-full px-4 py-2.5 bg-cream text-plum-deep text-sm placeholder:text-plum-deep/50 focus:outline-none" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="supply-phone" className="w-full rounded-full px-4 py-2.5 bg-cream text-plum-deep text-sm placeholder:text-plum-deep/50 focus:outline-none" />
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Delivery address *" data-testid="supply-address" className="w-full rounded-full px-4 py-2.5 bg-cream text-plum-deep text-sm placeholder:text-plum-deep/50 focus:outline-none" />
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" rows={2} data-testid="supply-notes" className="w-full rounded-2xl px-4 py-2.5 bg-cream text-plum-deep text-sm placeholder:text-plum-deep/50 focus:outline-none resize-none" />
              <label className="flex items-center gap-2.5 bg-cream/10 rounded-xl px-3.5 py-2.5 cursor-pointer text-sm" data-testid="supply-pay-card">
                <input type="radio" name="supply-pay" checked={paymentMethod === "card"} onChange={() => setPaymentMethod("card")} className="accent-lemon" />
                <CreditCard className="w-4 h-4 text-lemon" /> PAY NOW BY CARD
              </label>
              <label className="flex items-center gap-2.5 bg-cream/10 rounded-xl px-3.5 py-2.5 cursor-pointer text-sm" data-testid="supply-pay-bank">
                <input type="radio" name="supply-pay" checked={paymentMethod === "bank_transfer"} onChange={() => setPaymentMethod("bank_transfer")} className="accent-lemon" />
                <Landmark className="w-4 h-4 text-lemon" /> BANK TRANSFER
              </label>
              <button type="submit" disabled={submitting} data-testid="supply-place-order"
                className="w-full rounded-full bg-lemon text-plum-deep py-3.5 font-serif-display uppercase text-sm tracking-[0.16em] font-bold hover:bg-cream transition-colors disabled:opacity-50 active:scale-[0.98]">
                {submitting ? "PLANTING YOUR ORDER…" : paymentMethod === "card" ? "CHECK OUT & PAY" : "PLACE ORDER"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
